import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { AlegraSyncButton } from '../components/AlegraSyncButton';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from '../hooks/use-toast';
import {
  RefreshCw, Search, TrendingDown, Calendar, ChevronDown, FileSpreadsheet,
  BadgeCheck, Mail, Copy, Phone, Pencil, Save, X, Printer,
  Download, Eye, FileText, ExternalLink, FileDown, Loader2, AlertTriangle,
  CheckCircle, Package,
} from 'lucide-react';
import { exportToCSV, printTable } from '../lib/printUtils';

export const CuentasPorPagar = () => {
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [showPeriodPicker, setShowPeriodPicker] = useState(false);
  const [period, setPeriod] = useState({ desde: '2000-01-01', hasta: new Date().toISOString().split('T')[0] });
  const [periodLabel, setPeriodLabel] = useState('Desde el Inicio');
  const [docModal, setDocModal] = useState({ open: false, loading: false, data: null });
  const [confirmPay, setConfirmPay] = useState(null); // {bill}
  const [paying, setPaying] = useState(false);
  const [totals, setTotals] = useState({ 
    pendiente: 0, 
    vencidas30: 0,
    vencidas60: 0,
    vencidas90: 0,
    vencidas91plus: 0,
    noVencidas: 0
  });

  const handleViewDocument = useCallback(async (filename) => {
    setDocModal({ open: true, loading: true, data: null });
    try {
      const res = await api.get(`/sharepoint/buscar-documento?filename=${encodeURIComponent(filename)}`);
      setDocModal({ open: true, loading: false, data: res.data?.found ? res.data : { found: false, filename } });
    } catch {
      setDocModal({ open: true, loading: false, data: { found: false, filename } });
    }
  }, []);

  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  const getDaysOverdue = (dueDate) => {
    if (!dueDate) return 0;
    const due = new Date(dueDate);
    const today = new Date();
    const diff = Math.floor((today - due) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/alegra/cuentas-por-pagar?fecha_desde=${period.desde}&fecha_hasta=${period.hasta}`);
      const data = res.data;
      
      setBills(data.facturas || []);
      setTotals(data.totales || {
        pendiente: 0,
        vencidas30: 0,
        vencidas60: 0,
        vencidas90: 0,
        vencidas91plus: 0,
        noVencidas: 0
      });
    } catch (err) { console.error('caught:', err); } finally {
      setLoading(false);
    }
  };

  const selectPeriod = (label, desde, hasta) => {
    setPeriod({ desde, hasta });
    setPeriodLabel(label);
    setShowPeriodPicker(false);
  };

  const ejecutarPagoAlegra = async () => {
    if (!confirmPay?.bill) return;
    const bill = confirmPay.bill;
    setPaying(true);
    try {
      const res = await api.post(
        `/alegra/cuentas-por-pagar/${bill.alegra_id}/marcar-pagada`,
        {}
      );
      const payId = res.data?.alegra_payment_id;
      toast({
        title: `Pago registrado en Alegra · ${bill.numero || bill.alegra_id}`,
        description: payId ? `Payment-out #${payId} · ${bill.proveedor_nombre || ''}` : '',
      });
      setConfirmPay(null);
      loadData();
    } catch (err) {
      toast({
        title: 'No se pudo registrar el pago',
        description: err?.response?.data?.detail || 'Error Alegra',
        variant: 'destructive',
      });
    } finally {
      setPaying(false);
    }
  };

  const getPeriodPresets = () => {
    const today = new Date();
    const formatDate = (d) => d.toISOString().split('T')[0];
    
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay() + 1);
    
    const startOfLastWeek = new Date(startOfWeek);
    startOfLastWeek.setDate(startOfLastWeek.getDate() - 7);
    const endOfLastWeek = new Date(startOfWeek);
    endOfLastWeek.setDate(endOfLastWeek.getDate() - 1);
    
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const startOfLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const endOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
    
    const startOfQuarter = new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3, 1);
    const startOfLastQuarter = new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3 - 3, 1);
    const endOfLastQuarter = new Date(startOfQuarter);
    endOfLastQuarter.setDate(endOfLastQuarter.getDate() - 1);
    
    const startOfYear = new Date(today.getFullYear(), 0, 1);
    const startOfLastYear = new Date(today.getFullYear() - 1, 0, 1);
    const endOfLastYear = new Date(today.getFullYear() - 1, 11, 31);
    
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    return [
      { label: 'Desde el Inicio', desde: '2000-01-01', hasta: formatDate(today) },
      { label: 'Hoy', desde: formatDate(today), hasta: formatDate(today) },
      { label: 'Ayer', desde: formatDate(yesterday), hasta: formatDate(yesterday) },
      { label: 'Esta Semana', desde: formatDate(startOfWeek), hasta: formatDate(today) },
      { label: 'Semana Anterior', desde: formatDate(startOfLastWeek), hasta: formatDate(endOfLastWeek) },
      { label: 'Este Mes', desde: formatDate(startOfMonth), hasta: formatDate(today) },
      { label: 'Mes Anterior', desde: formatDate(startOfLastMonth), hasta: formatDate(endOfLastMonth) },
      { label: 'Este Trimestre', desde: formatDate(startOfQuarter), hasta: formatDate(today) },
      { label: 'Trimestre Anterior', desde: formatDate(startOfLastQuarter), hasta: formatDate(endOfLastQuarter) },
      { label: 'Este Año', desde: formatDate(startOfYear), hasta: formatDate(today) },
      { label: 'Año Anterior', desde: formatDate(startOfLastYear), hasta: formatDate(endOfLastYear) },
    ];
  };

  const getFilteredBills = () => {
    let filtered = bills;
    
    // Filter by tab
    if (activeTab !== 'all') {
      filtered = filtered.filter(bill => {
        const days = getDaysOverdue(bill.fecha_vencimiento);
        switch (activeTab) {
          case 'noVencidas': return days <= 0;
          case 'vencidas30': return days > 0 && days <= 30;
          case 'vencidas60': return days > 30 && days <= 60;
          case 'vencidas90': return days > 60 && days <= 90;
          case 'vencidas91plus': return days > 90;
          default: return true;
        }
      });
    }
    
    // Filter by search
    if (search) {
      filtered = filtered.filter(bill => 
        bill.numero?.toLowerCase().includes(search.toLowerCase()) ||
        bill.proveedor_nombre?.toLowerCase().includes(search.toLowerCase())
      );
    }
    
    return filtered;
  };

  const filteredBills = getFilteredBills();

  // Carlos (10/05/2026): imprimir y exportar CxP
  const fmtCLPNum = (n) => Math.round(Number(n) || 0).toLocaleString('es-CL');
  const imprimirCxP = () => {
    if (!filteredBills.length) { toast({ title: 'No hay facturas para imprimir' }); return; }
    const totalNeto = filteredBills.reduce((s, b) => s + (Number(b.total) || 0), 0);
    const totalSaldo = filteredBills.reduce((s, b) => s + (Number(b.saldo) || 0), 0);
    printTable({
      title: 'Cuentas por Pagar',
      subtitle: `${filteredBills.length} facturas · Período ${period.desde} → ${period.hasta} · Tab: ${activeTab}`,
      summary: [
        { label: 'Total facturado', value: `$${fmtCLPNum(totalNeto)}`, color: '#1d4ed8' },
        { label: 'Total saldo', value: `$${fmtCLPNum(totalSaldo)}`, color: '#b91c1c', highlight: true },
        { label: 'Total pagado', value: `$${fmtCLPNum(totalNeto - totalSaldo)}`, color: '#047857' },
      ],
      columns: [
        { key: 'proveedor_nombre', label: 'Proveedor', format: (v, r) => v || r.proveedor_id || '' },
        { key: 'proveedor_rut', label: 'RUT', mono: true },
        { key: 'numero', label: 'Folio', mono: true, align: 'center' },
        { key: 'fecha', label: 'Emisión' },
        { key: 'fecha_vencimiento', label: 'Vencimiento' },
        { key: 'total', label: 'Total', align: 'right', mono: true, format: (v) => `$${fmtCLPNum(v)}` },
        { key: 'saldo', label: 'Saldo', align: 'right', mono: true, format: (v) => `$${fmtCLPNum(v)}` },
        { key: 'estado', label: 'Estado', align: 'center', format: (v) => (v || '').toUpperCase() },
      ],
      rows: filteredBills,
    });
  };

  const exportarCxPExcel = () => {
    if (!filteredBills.length) { toast({ title: 'No hay facturas para exportar' }); return; }
    exportToCSV({
      filename: `cuentas_por_pagar_${period.desde}_${period.hasta}`,
      title: 'CHIRINO STEEL · Cuentas por Pagar',
      summary: [
        { label: 'Total facturas', value: filteredBills.length },
        { label: 'Período desde', value: period.desde },
        { label: 'Período hasta', value: period.hasta },
        { label: 'Tab activo', value: activeTab },
        { label: 'Total facturado (CLP)', value: Math.round(filteredBills.reduce((s, b) => s + (Number(b.total) || 0), 0)) },
        { label: 'Total saldo (CLP)', value: Math.round(filteredBills.reduce((s, b) => s + (Number(b.saldo) || 0), 0)) },
        { label: 'Generado', value: new Date().toLocaleString('es-CL') },
      ],
      columns: [
        { key: 'proveedor_nombre', label: 'Proveedor' },
        { key: 'proveedor_rut', label: 'RUT proveedor' },
        { key: 'numero', label: 'Folio' },
        { key: 'fecha', label: 'Fecha emisión' },
        { key: 'fecha_vencimiento', label: 'Fecha vencimiento' },
        { key: 'total', label: 'Total (CLP)', format: (v) => Math.round(Number(v) || 0) },
        { key: 'saldo', label: 'Saldo (CLP)', format: (v) => Math.round(Number(v) || 0) },
        { key: 'estado', label: 'Estado' },
        { key: 'observaciones', label: 'Observaciones' },
      ],
      rows: filteredBills,
    });
  };
  const fmt = (n) => Math.round(n || 0).toLocaleString('es-CL');
  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  };

  const tabs = [
    { id: 'vencidas30', label: 'Vencidas 30 días o menos', value: totals.vencidas30 },
    { id: 'vencidas60', label: 'Vencidas 31 días a 60 días', value: totals.vencidas60 },
    { id: 'vencidas90', label: 'Vencidas 61 días a 90 días', value: totals.vencidas90 },
    { id: 'vencidas91plus', label: 'Vencidas 91+', value: totals.vencidas91plus },
    { id: 'noVencidas', label: 'No vencidas', value: totals.noVencidas },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Cuentas por Pagar</h1>
          <p className="text-sm text-muted-foreground">
            Conoce las deudas que tienes registradas y lleva un control de tus pagos pendientes.
          </p>
        </div>
        <div className="flex gap-2">
          <AlegraSyncButton onSuccess={() => loadData()} />
          <Button variant="outline" size="sm" onClick={imprimirCxP} title="Imprimir listado" data-testid="btn-print-cxp">
            <Printer className="w-3.5 h-3.5 mr-1" /> Imprimir
          </Button>
          <Button variant="outline" size="sm" onClick={exportarCxPExcel} className="text-emerald-700 border-emerald-300 hover:bg-emerald-50" title="Exportar a Excel" data-testid="btn-excel-cxp">
            <FileSpreadsheet className="w-3.5 h-3.5 mr-1" /> Excel
          </Button>
          <Button onClick={loadData} disabled={loading} className="bg-teal-500 hover:bg-teal-600">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
        </div>
      </div>

      {/* Period Selector */}
      <div className="bg-white rounded-lg border p-4">
        <label className="block text-sm font-medium mb-2">
          Período <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <button
            onClick={() => setShowPeriodPicker(!showPeriodPicker)}
            className="flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50 text-left min-w-[250px]"
          >
            <Calendar className="w-4 h-4 text-gray-400" />
            <span>{formatDisplayDate(period.desde)} - {formatDisplayDate(period.hasta)}</span>
            <ChevronDown className="w-4 h-4 text-gray-400 ml-auto" />
          </button>
          
          {showPeriodPicker && (
            <div className="absolute top-full left-0 mt-1 bg-white border rounded-lg shadow-lg z-50 p-4 min-w-[300px]">
              <div className="font-medium text-sm mb-2">Predefinido</div>
              <div className="space-y-1">
                {getPeriodPresets().map((preset) => (
                  <button
                    key={preset.label}
                    onClick={() => selectPeriod(preset.label, preset.desde, preset.hasta)}
                    className={`block w-full text-left px-3 py-2 text-sm rounded hover:bg-teal-50 ${
                      periodLabel === preset.label ? 'bg-teal-100 text-teal-700' : ''
                    }`}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
              <div className="border-t mt-3 pt-3">
                <div className="text-sm text-gray-500 mb-2">Rango personalizado:</div>
                <div className="flex gap-2">
                  <input
                    type="date"
                    value={period.desde}
                    onChange={(e) => setPeriod({...period, desde: e.target.value})}
                    className="flex-1 px-2 py-1 border rounded text-sm"
                  />
                  <span className="self-center text-gray-400">-</span>
                  <input
                    type="date"
                    value={period.hasta}
                    onChange={(e) => setPeriod({...period, hasta: e.target.value})}
                    className="flex-1 px-2 py-1 border rounded text-sm"
                  />
                </div>
                <Button 
                  size="sm" 
                  className="w-full mt-2 bg-teal-500 hover:bg-teal-600"
                  onClick={() => { setPeriodLabel('Personalizado'); setShowPeriodPicker(false); }}
                >
                  Aplicar
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tabs de vencimiento */}
      <div className="bg-gray-50 rounded-lg p-1">
        <div className="flex">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(activeTab === tab.id ? 'all' : tab.id)}
              className={`flex-1 py-4 px-3 text-center transition-all rounded-lg ${
                activeTab === tab.id 
                  ? 'bg-teal-500 text-white shadow-lg' 
                  : 'bg-white text-gray-600 hover:bg-gray-100'
              }`}
            >
              <span className="text-sm font-medium block">{tab.label}</span>
              {activeTab === tab.id && (
                <span className="text-lg font-bold mt-1 block">${fmt(tab.value)}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Total por pagar */}
      <div className="bg-white rounded-xl shadow-sm border p-8 text-center">
        <div className="flex items-center justify-center gap-4">
          <TrendingDown className="w-8 h-8 text-gray-400" />
          <div>
            <p className="text-gray-500 text-lg">Total por pagar</p>
            <p className="text-4xl font-bold text-red-500">${fmt(totals.pendiente)}</p>
          </div>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="flex justify-between items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por N° factura o proveedor..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-muted-foreground">
            Mostrando {filteredBills.length} facturas
          </span>
          <Button variant="outline" size="sm">
            Filtrar
          </Button>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-8">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto text-primary" />
          <p className="text-muted-foreground mt-2">Cargando facturas...</p>
        </div>
      ) : (
        <div className="border rounded-lg" style={{ overflowX: 'clip' }}>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b sticky top-14 lg:top-0 z-20">
              <tr>
                <th className="p-3 text-left font-medium text-gray-600">Tipo de documento</th>
                <th className="p-3 text-left font-medium text-gray-600">Número</th>
                <th className="p-3 text-left font-medium text-gray-600">Proveedor</th>
                <th className="p-3 text-center font-medium text-gray-600">OC</th>
                <th className="p-3 text-center font-medium text-gray-600">Recepción</th>
                <th className="p-3 text-center font-medium text-gray-600">Creación</th>
                <th className="p-3 text-center font-medium text-gray-600">Vencimiento</th>
                <th className="p-3 text-right font-medium text-gray-600">Total</th>
                <th className="p-3 text-right font-medium text-gray-600">Pagado</th>
                <th className="p-3 text-right font-medium text-gray-600">Por pagar</th>
                <th className="p-3 text-center font-medium text-gray-600">Doc</th>
                <th className="p-3 text-center font-medium text-gray-600">Acción</th>
              </tr>
            </thead>
            <tbody>
              {filteredBills.length === 0 ? (
                <tr>
                  <td colSpan="12" className="p-8 text-center text-muted-foreground">
                    {search ? 'No se encontraron facturas' : 'No hay facturas pendientes de pago'}
                  </td>
                </tr>
              ) : (
                filteredBills.slice(0, 100).map((bill, idx) => {
                  const daysOverdue = getDaysOverdue(bill.fecha_vencimiento);
                  const pagado = (bill.total || 0) - (bill.saldo || 0);
                  const docFilename = bill.ruta_archivo ? bill.ruta_archivo.split('\\').pop().split('/').pop() : null;
                  return (
                    <tr key={idx} className="border-b hover:bg-gray-50" data-testid={`cxp-row-${bill.numero || idx}`}>
                      <td className="p-3 text-teal-600">Factura de proveedor</td>
                      <td className="p-3 font-mono text-blue-600">{bill.numero || bill.alegra_id}</td>
                      <td className="p-3 max-w-xs truncate" title={bill.proveedor_nombre}>{bill.proveedor_nombre || '-'}</td>
                      <td className="p-3 text-center">
                        {bill.estado_oc === 'Con OC' ? (
                          <span className="inline-flex items-center gap-0.5 bg-green-100 text-green-700 px-2 py-0.5 rounded text-[10px] font-medium" data-testid={`oc-badge-${bill.numero}`}>
                            <Package className="w-3 h-3" /> {bill.numero_oc}
                          </span>
                        ) : (
                          <span className="text-gray-300 text-[10px]">-</span>
                        )}
                      </td>
                      <td className="p-3 text-center">
                        {bill.estado_recepcion === 'Recepcionada' ? (
                          <span className="inline-flex items-center gap-0.5 bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-[10px] font-medium" data-testid={`rec-badge-${bill.numero}`}>
                            <CheckCircle className="w-3 h-3" /> Si
                          </span>
                        ) : (
                          <span className="text-gray-300 text-[10px]">-</span>
                        )}
                      </td>
                      <td className="p-3 text-center text-gray-600">{bill.fecha ? formatDisplayDate(bill.fecha) : '-'}</td>
                      <td className="p-3 text-center">
                        {bill.fecha_vencimiento ? (
                          <span className={daysOverdue > 0 ? 'text-orange-500' : 'text-gray-600'}>
                            {formatDisplayDate(bill.fecha_vencimiento)}
                          </span>
                        ) : '-'}
                      </td>
                      <td className="p-3 text-right font-mono">${fmt(bill.total)}</td>
                      <td className="p-3 text-right font-mono text-gray-500">${fmt(pagado)}</td>
                      <td className="p-3 text-right font-mono font-bold">${fmt(bill.saldo)}</td>
                      <td className="p-3 text-center">
                        {docFilename ? (
                          <button onClick={() => handleViewDocument(docFilename)}
                            className="inline-flex items-center gap-0.5 text-blue-600 hover:text-blue-800 text-[10px] bg-blue-50 px-2 py-1 rounded"
                            data-testid={`doc-btn-${bill.numero}`}>
                            <Eye className="w-3 h-3" />
                          </button>
                        ) : (
                          <span className="text-gray-300 text-[10px]">-</span>
                        )}
                      </td>
                      <td className="p-3 text-center">
                        {(bill.saldo || 0) > 0 ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 px-2 text-xs text-emerald-700 hover:bg-emerald-50 gap-1"
                            onClick={() => setConfirmPay({ bill })}
                            title="Marcar pagada y registrar en Alegra"
                            data-testid={`btn-pagar-bill-${bill.alegra_id}`}
                          >
                            <BadgeCheck className="w-3.5 h-3.5" />
                            Pagar
                          </Button>
                        ) : (
                          <span className="text-emerald-600 text-[10px] font-medium">✓ pagada</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
            {filteredBills.length > 0 && (
              <tfoot className="bg-gray-100 font-bold">
                <tr>
                  <td colSpan="7" className="p-3 text-right">TOTALES:</td>
                  <td className="p-3 text-right font-mono">
                    ${fmt(filteredBills.reduce((sum, bill) => sum + (bill.total || 0), 0))}
                  </td>
                  <td className="p-3 text-right font-mono text-gray-500">
                    ${fmt(filteredBills.reduce((sum, bill) => sum + ((bill.total || 0) - (bill.saldo || 0)), 0))}
                  </td>
                  <td className="p-3 text-right font-mono text-red-600">
                    ${fmt(filteredBills.reduce((sum, bill) => sum + (bill.saldo || 0), 0))}
                  </td>
                  <td></td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}

      {/* Document Viewer Modal */}
      <Dialog open={docModal.open} onOpenChange={(open) => !docModal.loading && setDocModal({ ...docModal, open })}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" /> {docModal.data?.filename || 'Documento'}
            </DialogTitle>
          </DialogHeader>
          {docModal.loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              <span className="ml-2 text-gray-600">Buscando en SharePoint...</span>
            </div>
          )}
          {docModal.data && !docModal.data.found && (
            <div className="space-y-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded p-4 text-yellow-800">
                <AlertTriangle className="w-5 h-5 inline mr-2" />
                Documento <strong>{docModal.data.filename}</strong> no encontrado en SharePoint.
              </div>
              <div className="text-center py-4">
                <FileText className="w-16 h-16 mx-auto mb-3 text-gray-300" />
                <p className="text-gray-600 mb-4">El documento puede estar en la carpeta de SharePoint.</p>
                <Button onClick={() => window.open('https://chirinosteelcom.sharepoint.com/:f:/s/ServidorChsteel/IgCijncVJt1bRZy1oVHJFg41Ae24HqfThKmBpQ_OMKYpy2w?e=KUf46I', '_blank')}
                  className="flex items-center gap-2 mx-auto bg-blue-600 hover:bg-blue-700">
                  <ExternalLink className="w-4 h-4" /> Abrir Carpeta en SharePoint
                </Button>
              </div>
            </div>
          )}
          {docModal.data?.found && (
            <div className="space-y-4">
              {docModal.data.previewUrl ? (
                <div className="border rounded-lg overflow-hidden bg-gray-100" style={{ height: '500px' }}>
                  <iframe src={docModal.data.previewUrl} className="w-full h-full" title="Vista previa" />
                </div>
              ) : (
                <div className="bg-gray-100 rounded-lg p-8 text-center text-gray-500">
                  <FileText className="w-16 h-16 mx-auto mb-3 text-gray-300" />
                  <p>Vista previa no disponible</p>
                </div>
              )}
              <div className="flex gap-3 justify-center">
                {docModal.data.webUrl && (
                  <Button variant="outline" onClick={() => window.open(docModal.data.webUrl, '_blank')}>
                    <ExternalLink className="w-4 h-4 mr-2" /> Abrir en SharePoint
                  </Button>
                )}
                <Button onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/sharepoint/descargar/${docModal.data.file_id}`, '_blank')}
                  className="bg-blue-600 hover:bg-blue-700">
                  <FileDown className="w-4 h-4 mr-2" /> Descargar PDF
                </Button>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDocModal({ open: false, loading: false, data: null })}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AlertDialog confirmar pago en Alegra */}
      <AlertDialog open={!!confirmPay} onOpenChange={(o) => !o && !paying && setConfirmPay(null)}>
        <AlertDialogContent data-testid="dialog-pagar-bill-alegra" className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <BadgeCheck className="w-5 h-5 text-emerald-600" />
              ¿Marcar factura como PAGADA?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                {confirmPay?.bill && (
                  <div className="bg-slate-50 dark:bg-slate-900/40 border rounded p-2.5 space-y-1">
                    <div className="flex justify-between"><span className="text-muted-foreground">Proveedor:</span><span className="font-medium truncate ml-2 max-w-[220px]" title={confirmPay.bill.proveedor_nombre}>{confirmPay.bill.proveedor_nombre || '—'}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Folio:</span><span className="font-mono font-semibold">{confirmPay.bill.numero || confirmPay.bill.alegra_id}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Saldo a pagar:</span><span className="font-bold tabular-nums text-emerald-700">${fmt(confirmPay.bill.saldo)}</span></div>
                  </div>
                )}
                <ul className="list-disc pl-5 text-[13px] text-muted-foreground space-y-0.5">
                  <li>Se registrará un <strong>payment-out</strong> en <strong>Alegra</strong> (Banco de Chile 1) por el saldo pendiente.</li>
                  <li>La factura quedará marcada como pagada en Alegra y desaparecerá de esta lista en el próximo refresh.</li>
                  <li>Si Alegra rechaza el pago, NO se modifica nada localmente y verás un toast con el error.</li>
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={paying} data-testid="btn-cancel-pagar-bill">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); ejecutarPagoAlegra(); }}
              disabled={paying}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="btn-confirm-pagar-bill"
            >
              {paying ? 'Procesando…' : 'Sí, pagar en Alegra'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CuentasPorPagar;
